/**
 * @file AliTableCell/index.tsx
 * @description 表格 body 单元格总入口（第 4 层 — 调度层）。
 *
 * 流程：取 row[column].value → getCellText 得 text → 按 column.cellType 选子组件。
 * 条件格式相关 cellType 在第 3 步建列时已写好，此处只做 map 查找、不再判断 bar/数值。
 * 合并单元格 rowSpan 由 column.getCellProps 注入，由 BaseTable SpanManager 统一处理。
 */
import React from 'react';
import StatisticsBubble from "./StatisticsBubble";
import UrlCell from "@customComp/AliTableAnalytics/components/AliTableCell/UrlCell";
import BarPercentCell from "@customComp/AliTableAnalytics/components/AliTableCell/BarPercentCell";
import BarPercentNumericalCell from '@customComp/AliTableAnalytics/components/AliTableCell/BarPercentNumericalCell';
import NumericalConditionCell from '@customComp/AliTableAnalytics/components/AliTableCell/NumericalConditionCell';
import CompareCell from '@customComp/AliTableAnalytics/components/AliTableCell/CompareCell';
import {
  AliTableCellTypeEnum,
  IAliTableCellComponentProps,
  TAliTableColumn,
} from "@customComp/AliTableAnalytics/types";
import { getCellText } from "@customComp/AliTableAnalytics/utils";
import { getLanguage } from '@home/utils/common';

interface IAliTableCellProps {
  row: any;
  column: TAliTableColumn;
  tdProps: any;
  colIndex: number;
  rowIndex: number;
}

// 普通单元格组件
const NormalCell: React.FC<IAliTableCellComponentProps> = ({ text }) => <>{text}</>;

/** 一种 cellType 对应一个展示组件；条件格式占 3 项（见第 3 步决策表） */
const CellComponentMap: Record<AliTableCellTypeEnum, React.FC<IAliTableCellComponentProps>> = {
  [AliTableCellTypeEnum.Url]: UrlCell,
  [AliTableCellTypeEnum.Original]: NormalCell,
  [AliTableCellTypeEnum.BarPercent]: BarPercentCell,
  [AliTableCellTypeEnum.NumericalCondition]: NumericalConditionCell,
  [AliTableCellTypeEnum.BarPercentNumerical]: BarPercentNumericalCell,
  [AliTableCellTypeEnum.Compare]: CompareCell,
};

const AliTableCell: React.FC<IAliTableCellProps> = ({ row, column, tdProps = {} }) => {
  const { cellType = AliTableCellTypeEnum.Original, isCompareCell, isEn } = column;
  // @ts-ignore
  const { value: val, value_en: valueEn, value_cn: valueCn, value_type: valueType, is_statistics: isStatistics, statistics_name: statisticsName } = row?.[column?.code] || {};
  const value = getLanguage(isEn, valueEn, valueCn) || val;

  // 统计行标签列：后端在 table_data 行内标记 is_statistics + statistics_name（非 extra_table_data）
  if (isStatistics && statisticsName) {
    return (
      <td {...tdProps}>
        <StatisticsBubble statistics={statisticsName} />
      </td>
    );
  }

  // null和undefined以及统计值为空字符串的单元格展示为-
  // 统计指标行的无数值情况展示-，不展示为空
  // isCompareCell为true时，不展示为-，按对比单元格渲染逻辑处理
  if ((value === null || value === undefined || (isStatistics && !statisticsName && value === "")) && !isCompareCell) {
    return <td {...tdProps}>-</td>;
  }

  // 处理单元格内容文本展示
  // 优先使用valueType，如果valueType为空，则使用column的valueType(支持列中不同行单元格按不同格式渲染)
  const text = getCellText(value, { ...(column), valueType: valueType || column?.valueType });

  const Component = CellComponentMap[cellType] || NormalCell;

  return (
    <td {...tdProps}>
      <Component text={text} row={row} column={column} />
    </td>
  );
};

export default AliTableCell;
